import { umbrellaException } from "../../shared/lib/umbrellaException"
import { runInAction } from "mobx"
import { type CourseStore, courseStore } from "../store"
import { type CourseDto } from "../../common"
import {
  createCourse,
  getCourseById,
  getCourses,
  removeCourse,
  toggleCoursePublish,
  updateCourse,
} from "../../shared/api/courses"

class CourseServiceImpl {

  async load(): Promise<void> {
    try {
      runInAction(() => {
        this.courseStore.courses = []
        this.courseStore.isLoading = true
        this.courseStore.error = undefined
        this.courseStore.searchParams.page = 1
      })

      const {
        data: {
          totalCount,
          courses,
        },
      } = await getCourses(this.courseStore.searchParams)

      runInAction(() => {
        this.courseStore.totalCount = totalCount
        this.courseStore.courses = courses
        this.changePage()
      })
    } catch (e) {
      this.courseStore.error = umbrellaException(e)
    } finally {
      runInAction(() => {
        this.courseStore.isLoading = false
      })
    }
  }

  async loadMore(): Promise<void> {
    if (this.courseStore.isLoading) {
      return
    }

    try {
      runInAction(() => {
        this.courseStore.isLoading = true
        this.courseStore.error = undefined
      })

      const { data: { totalCount, courses } } = await getCourses(this.courseStore.searchParams)

      runInAction(() => {
        this.courseStore.totalCount = totalCount
        this.courseStore.courses = [ ...this.courseStore.courses, ...courses ]
        this.changePage()
      })
    } catch (e) {
      this.courseStore.error = umbrellaException(e)
    } finally {
      runInAction(() => {
        this.courseStore.isLoading = false
      })
    }
  }

  togglePublish = async (courseId: string, state: boolean): Promise<void> => {
    try {
      runInAction(() => {
        this.courseStore.isLoading = true
        this.courseStore.error = undefined
      })

      const { data } = await toggleCoursePublish(courseId, state)

      runInAction(() => {
        const current = this.courseStore.courses.find((course) => course._id === courseId)

        if (current !== undefined) {
          current.published = data.published
        }
      })
    } catch (err) {
      runInAction(() => {
        this.courseStore.error = umbrellaException(err)
      })
    } finally {
      runInAction(() => {
        this.courseStore.isLoading = false
      })
    }
  }

  async getById(courseId: string): Promise<CourseDto | undefined> {
    try {
      runInAction(() => {
        this.courseStore.isLoading = true
        this.courseStore.error = undefined
      })

      const { data } = await getCourseById(courseId)

      return data
    } catch (err) {
      runInAction(() => {
        this.courseStore.error = umbrellaException(err)
      })
    } finally {
      runInAction(() => {
        this.courseStore.isLoading = false
      })
    }
  }

  async create(data: FormData): Promise<boolean> {
    try {
      runInAction(() => {
        this.courseStore.isLoading = true
        this.courseStore.error = undefined
      })

      await createCourse(data)
      return true
    } catch (err) {
      runInAction(() => {
        this.courseStore.error = umbrellaException(err)
      })
      return false
    } finally {
      runInAction(() => {
        this.courseStore.isLoading = false
      })
    }
  }

  async update(courseId: string, data: FormData): Promise<boolean> {
    try {
      runInAction(() => {
        this.courseStore.isLoading = true
        this.courseStore.error = undefined
      })

      await updateCourse(courseId, data)
      return true
    } catch (err) {
      runInAction(() => {
        this.courseStore.error = umbrellaException(err)
      })
      return false
    } finally {
      runInAction(() => {
        this.courseStore.isLoading = false
      })
    }
  }

  async remove(courseId: string): Promise<void> {
    try {
      runInAction(() => {
        this.courseStore.isLoading = true
        this.courseStore.error = undefined
      })

      await removeCourse(courseId)

      runInAction(() => {
        this.courseStore.courses = this.courseStore.courses.filter((course) => course._id !== courseId)
        this.courseStore.totalCount -= 1
      })
    } catch (err) {
      runInAction(() => {
        this.courseStore.error = umbrellaException(err)
      })
    } finally {
      runInAction(() => {
        this.courseStore.isLoading = false
      })
    }
  }

  setSearchParam<KEY extends keyof CourseStore["searchParams"], VALUE extends CourseStore["searchParams"][KEY]>(key: KEY, value: VALUE): void {
    runInAction(() => {
      this.courseStore.searchParams[key] = value
    })

    void this.load()
  }

  private changePage(): void {
    if (this.courseStore.totalCount > this.courseStore.courses.length) {
      this.courseStore.searchParams.page += 1
    }
  }

  constructor(
    private courseStore: CourseStore,
  ) {
  }
}

export const CourseService = new CourseServiceImpl(courseStore)
